import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Send, ArrowLeft, CheckCircle2, Paperclip, X } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";

const API = process.env.REACT_APP_BACKEND_URL;

const inputCls =
  "w-full rounded-xl border border-[#d4af37]/25 bg-[#050b1d]/70 px-4 py-3 font-body text-sm text-white placeholder:text-white/30 focus:border-[#d4af37] focus:outline-none transition-colors duration-300";

const BG_QUESTIONS = [
  { key: "owes_government", label: "Do you owe any government agency?" },
  { key: "has_judgements", label: "Do you have any judgements against you?" },
  { key: "felony_10yr", label: "Have you been convicted of a felony in the past 10 years?" },
  { key: "identity_theft", label: "Have you ever been convicted of identity theft?" },
  { key: "license_revoked", label: "Have you ever had a professional license suspended or revoked?" },
];

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  address: "",
  city: "",
  state: "",
  zip_code: "",
  birthday: "",
  owes_government: false,
  has_judgements: false,
  felony_10yr: false,
  identity_theft: false,
  license_revoked: false,
  employment_history: "",
  held_1099: "",
  licensed: "",
  experience: "",
  business_consulting: "",
  signature: "",
  signature_date: "",
};

const SectionTitle = ({ children }) => (
  <div className="pt-2">
    <p className="font-body text-xs tracking-[0.3em] uppercase text-[#d4af37]">{children}</p>
    <div className="mt-2 h-px bg-gradient-to-r from-[#d4af37]/50 to-transparent" />
  </div>
);

export default function CareersApplyPage() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [resume, setResume] = useState(null);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.checked }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email, and phone number.");
      return;
    }
    if (!form.signature || !form.signature_date) {
      toast.error("Please sign and date your application before submitting.");
      return;
    }
    setSending(true);
    try {
      let resumeFields = {};
      if (resume) {
        const fd = new FormData();
        fd.append("file", resume);
        const upRes = await fetch(`${API}/api/careers/upload-resume`, { method: "POST", body: fd });
        if (!upRes.ok) {
          const err = await upRes.json().catch(() => ({}));
          toast.error(err.detail || "Resume upload failed. Please try a smaller PDF/DOC file.");
          setSending(false);
          return;
        }
        resumeFields = await upRes.json();
      }
      const res = await fetch(`${API}/api/careers/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, ...resumeFields }),
      });
      if (!res.ok) throw new Error("Request failed");
      setDone(true);
    } catch {
      toast.error("Something went wrong. Please try again or email us at info@heritagefamilypa.com.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      className="relative min-h-screen pt-32 pb-20"
      style={{ background: "linear-gradient(180deg, #071233 0%, #0a1a44 45%, #050b1d 100%)" }}
      data-testid="apply-page"
    >
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <Reveal>
          <div className="text-center">
            <img
              src="/logo-full.png"
              alt="Heritage Family Protection Agency"
              data-testid="apply-logo"
              className="mx-auto h-24 md:h-32 w-auto object-contain"
            />
            <p className="mt-6 font-body text-xs md:text-sm tracking-[0.3em] uppercase text-[#d4af37]">Careers — Path One</p>
            <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-white">
              Agent <span className="text-gold-gradient italic">application.</span>
            </h1>
            <p className="mt-4 mx-auto max-w-xl font-body font-light text-base md:text-lg text-white/60">
              Financial Services Agent — complete the application below, sign, and submit. Our team will review it and
              reach out soon.
            </p>
          </div>
        </Reveal>

        {done ? (
          <Reveal>
            <div data-testid="apply-success" className="mt-12 rounded-3xl border border-[#d4af37]/35 bg-[#050b1d]/60 p-10 md:p-14 text-center">
              <CheckCircle2 size={48} className="mx-auto text-[#d4af37]" strokeWidth={1.2} />
              <h2 className="mt-6 font-serif-display text-3xl md:text-4xl text-white">
                Application <span className="text-gold-gradient italic">received.</span>
              </h2>
              <p className="mt-4 font-body font-light text-base md:text-lg text-white/60 max-w-xl mx-auto">
                Thank you for applying to join Heritage Family Protection Agency. We've emailed you a confirmation, and
                our team will review your application and reach out soon.
              </p>
              <Link
                to="/careers"
                data-testid="apply-back-careers"
                className="mt-8 inline-flex items-center gap-2 rounded-full border border-[#d4af37]/40 px-7 py-3 font-body text-sm tracking-wide text-white/80 hover:text-[#fcf6ba] hover:border-[#d4af37] transition-colors duration-300"
              >
                <ArrowLeft size={15} /> Back to Careers
              </Link>
            </div>
          </Reveal>
        ) : (
          <Reveal delay={0.1}>
            <form
              onSubmit={submit}
              className="mt-12 rounded-3xl border border-[#d4af37]/30 bg-[#0a1633]/80 backdrop-blur p-8 md:p-10 space-y-6 shadow-[0_30px_90px_rgba(0,0,0,0.5)]"
              data-testid="apply-form"
            >
              <SectionTitle>Personal Information</SectionTitle>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <input data-testid="apply-name-input" className={inputCls} placeholder="Full name *" value={form.name} onChange={set("name")} />
                <input data-testid="apply-birthday-input" className={inputCls} placeholder="Birthday (MM/DD/YYYY)" value={form.birthday} onChange={set("birthday")} />
              </div>
              <input data-testid="apply-address-input" className={inputCls} placeholder="Street address" value={form.address} onChange={set("address")} />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <input data-testid="apply-city-input" className={inputCls} placeholder="City" value={form.city} onChange={set("city")} />
                <input data-testid="apply-state-input" className={inputCls} placeholder="State" value={form.state} onChange={set("state")} />
                <input data-testid="apply-zip-input" className={inputCls} placeholder="Zip code" value={form.zip_code} onChange={set("zip_code")} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <input data-testid="apply-email-input" type="email" className={inputCls} placeholder="Email address *" value={form.email} onChange={set("email")} />
                <input data-testid="apply-phone-input" className={inputCls} placeholder="Phone number *" value={form.phone} onChange={set("phone")} />
              </div>

              <SectionTitle>Background Check Questions</SectionTitle>
              <p className="font-body text-xs text-white/45 leading-relaxed -mt-2">
                Check the box for any question that applies to you. Leave unchecked if your answer is no.
              </p>
              <div className="space-y-3">
                {BG_QUESTIONS.map((q) => (
                  <label
                    key={q.key}
                    data-testid={`apply-bg-${q.key}`}
                    className="flex items-start gap-3 rounded-xl border border-[#d4af37]/20 bg-[#050b1d]/50 px-4 py-3.5 cursor-pointer hover:border-[#d4af37]/50 transition-colors duration-300"
                  >
                    <input
                      type="checkbox"
                      checked={form[q.key]}
                      onChange={toggle(q.key)}
                      className="mt-0.5 h-4 w-4 shrink-0 rounded accent-[#d4af37]"
                    />
                    <span className="font-body text-sm text-white/75 leading-snug">{q.label}</span>
                  </label>
                ))}
              </div>

              <SectionTitle>Employment History</SectionTitle>
              <textarea
                data-testid="apply-employment-input"
                rows={5}
                className={inputCls}
                placeholder="List your last 5 years of employment (employer, role, dates)"
                value={form.employment_history}
                onChange={set("employment_history")}
              />
              <select
                data-testid="apply-1099-select"
                className={`${inputCls} ${form.held_1099 ? "text-white" : "text-white/30"}`}
                value={form.held_1099}
                onChange={set("held_1099")}
              >
                <option value="" disabled>Have you ever held a 1099 (independent contractor) position before?</option>
                <option value="Yes" className="bg-[#0a1633] text-white">Yes</option>
                <option value="No" className="bg-[#0a1633] text-white">No</option>
              </select>

              <SectionTitle>Licensing &amp; Experience</SectionTitle>
              <select
                data-testid="apply-licensed-select"
                className={`${inputCls} ${form.licensed ? "text-white" : "text-white/30"}`}
                value={form.licensed}
                onChange={set("licensed")}
              >
                <option value="" disabled>Are you currently licensed in life &amp; health insurance?</option>
                <option value="Yes — actively licensed" className="bg-[#0a1633] text-white">Yes — actively licensed</option>
                <option value="No — but willing to get licensed" className="bg-[#0a1633] text-white">No — but willing to get licensed</option>
                <option value="Currently working on my license" className="bg-[#0a1633] text-white">Currently working on my license</option>
              </select>
              <select
                data-testid="apply-experience-select"
                className={`${inputCls} ${form.experience ? "text-white" : "text-white/30"}`}
                value={form.experience}
                onChange={set("experience")}
              >
                <option value="" disabled>Experience in insurance or financial services</option>
                <option value="No experience — ready to learn" className="bg-[#0a1633] text-white">No experience — ready to learn</option>
                <option value="Less than 1 year" className="bg-[#0a1633] text-white">Less than 1 year</option>
                <option value="1–3 years" className="bg-[#0a1633] text-white">1–3 years</option>
                <option value="3+ years" className="bg-[#0a1633] text-white">3+ years</option>
              </select>
              <select
                data-testid="apply-consulting-select"
                className={`${inputCls} ${form.business_consulting ? "text-white" : "text-white/30"}`}
                value={form.business_consulting}
                onChange={set("business_consulting")}
              >
                <option value="" disabled>Have you done business consulting before?</option>
                <option value="Yes" className="bg-[#0a1633] text-white">Yes</option>
                <option value="No" className="bg-[#0a1633] text-white">No</option>
              </select>

              <SectionTitle>Resume</SectionTitle>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.doc,.docx,.txt"
                className="hidden"
                data-testid="apply-resume-input"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f && f.size > 8 * 1024 * 1024) {
                    toast.error("Resume must be under 8 MB.");
                    e.target.value = "";
                    return;
                  }
                  setResume(f || null);
                }}
              />
              {resume ? (
                <div className="flex items-center justify-between gap-3 rounded-xl border border-[#d4af37]/40 bg-[#050b1d]/50 px-4 py-3.5" data-testid="apply-resume-attached">
                  <span className="flex items-center gap-2 font-body text-sm text-white/80 truncate">
                    <Paperclip size={15} className="shrink-0 text-[#d4af37]" /> {resume.name}
                  </span>
                  <button
                    type="button"
                    data-testid="apply-resume-remove"
                    onClick={() => { setResume(null); if (fileRef.current) fileRef.current.value = ""; }}
                    className="text-white/50 hover:text-red-400 transition-colors"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  data-testid="apply-resume-btn"
                  onClick={() => fileRef.current?.click()}
                  className="w-full flex items-center justify-center gap-2 rounded-xl border border-dashed border-[#d4af37]/40 bg-[#050b1d]/40 px-4 py-4 font-body text-sm text-white/60 hover:text-[#fcf6ba] hover:border-[#d4af37] transition-colors duration-300"
                >
                  <Paperclip size={15} /> Attach your resume (PDF, DOC, DOCX — optional)
                </button>
              )}

              <SectionTitle>Signature</SectionTitle>
              <p className="font-body text-xs text-white/45 leading-relaxed -mt-2">
                By typing your full legal name below, you certify that the information provided in this application is
                true and complete to the best of your knowledge.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <input
                  data-testid="apply-signature-input"
                  className={`${inputCls} font-serif-display italic text-lg`}
                  placeholder="Sign your full name *"
                  value={form.signature}
                  onChange={set("signature")}
                />
                <input
                  data-testid="apply-signature-date-input"
                  className={inputCls}
                  placeholder="Date (MM/DD/YYYY) *"
                  value={form.signature_date}
                  onChange={set("signature_date")}
                />
              </div>

              <button
                type="submit"
                disabled={sending}
                data-testid="apply-submit-btn"
                className="btn-sweep w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold-metallic px-8 py-4 font-body text-sm font-semibold tracking-wide text-black disabled:opacity-60"
              >
                {sending ? "Submitting..." : "Submit My Application"}
                <Send size={16} />
              </button>
              <p className="font-body text-[11px] leading-relaxed text-white/35 text-center">
                Your application is sent directly to our team at info@heritagefamilypa.com. We never sell your information.
              </p>
            </form>
          </Reveal>
        )}
      </div>
    </section>
  );
}
